<template>
  <div class="pa-3 hyp-main-container">
    <v-card v-if="!isLiveSearchVisualKeywordUnlocked" class="mb-3">
      <div class="px-3 py-3">
        <SearchFilterKeywords
          @change-keywords="handleChangeKeywords"
          @clear-keywords="handleClearKeywords"
          :lastReset="resetTimestamp"
        />
      </div>

      <div class="px-3" v-if="!isLiveSearchLocationFilterUnlocked">
        <v-radio-group
          v-model="boundsType"
          @change="handleChangeBounds"
          row
          dense
          class="bounds-radio-text ml-5"
        >
          <v-radio label="Map Extent" value="mapextent"></v-radio>
          <v-radio label="All Data" value="worldwide"></v-radio>
        </v-radio-group>
      </div>
    </v-card>

    <div>
      <v-expansion-panels v-model="filterPanelsOpen" accordion>
        <v-expansion-panel v-if="isLiveSearchVisualKeywordUnlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Keywords</span>
              <v-badge
                v-if="hasKeywordsFilterSet"
                title="There is an active filter in this panel"
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterKeywords
              @change-keywords="handleChangeKeywords"
              @clear-keywords="handleClearKeywords"
              @change-keywords-builder="handleChangeKeywordsBuilder"
              :inherited-keywords="keywords"
              :inherited-keywords-builder="keywordsBuilder"
              :lastReset="resetTimestamp"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- SAVED QUERIES FILTER -->
        <v-expansion-panel>
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Saved Queries</span>
              <v-badge
                v-if="selectedSavedSearch"
                title="There is an active filter in this panel"
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterSavedQueries
              :initialize="rememberedSavedSearchId"
              :reset="resetTimestamp"
              :selected="selectedSavedSearch"
              :defaultSavedSearchId="defaultSavedSearchId"
              @change="handleChangeSavedSearchQuery"
            ></SearchFilterSavedQueries>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- LOCATION FILTER -->
        <v-expansion-panel v-if="isLiveSearchLocationFilterUnlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Locations</span>
              <v-badge
                v-if="
                  (locationSelectionAoiId && locationSelectionAoiId.length) ||
                  (locationSelectionPlaces && locationSelectionPlaces.length)
                "
                :title="`There are active location filters in this panel`"
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterLocations
              :incoming-bounds-type="boundsType"
              :incoming-aoi-id="locationSelectionAoiId"
              :incoming-places="locationSelectionPlaces"
              :selected-units-radius="placesSelectionUnitsRadius"
              :reset="resetTimestamp"
              @change="handleChangeLocation"
            ></SearchFilterLocations>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- EVENTS FILTER -->
        <v-expansion-panel v-if="!isLiveSearchEventsFilterV2Unlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Events</span>
              <v-badge
                v-if="hasTopicsSelected"
                title="There are active filters in this panel."
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <EventsPicker
              :for-live-search="true"
              :topic-filter-counts="topicFilterCounts"
              :reset="resetTimestamp"
              :events-selection="eventsSelection"
              @changed-live-search="handleChangeEvents"
              @change-has-topics-selected="handleChangeHasTopicsSelected"
            ></EventsPicker>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- EVENTS 2.0 FILTER -->
        <v-expansion-panel v-if="isLiveSearchEventsFilterV2Unlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Events</span>

              <v-tooltip
                v-if="isLiveSearchAnalysisFilterUnlocked"
                top
                open-delay="1000"
                max-width="300"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs" class="switch-w-tooltip">
                    <v-badge
                      v-if="
                        contentType === 'events' &&
                        (eventsSelection ||
                          (sources && (sources.news || sources.social)))
                      "
                      :title="`There are active event and/or source filters selected in this panel`"
                      bordered
                      inline
                      class="ml-1"
                    ></v-badge>
                    <v-badge
                      v-if="
                        contentType &&
                        contentType !== 'events' &&
                        riskCategoriesSelection
                      "
                      :title="`There are active event and/or source filters selected in this panel`"
                      bordered
                      inline
                      color="#d0d0d0"
                      class="ml-1"
                    ></v-badge>
                  </div>
                </template>
                <span v-if="contentType === 'events'"
                  ><span class="application-heading"
                    >Searching for Events only.</span
                  >
                  <br />
                  To disable Events and show Analysis, enable an Analysis
                  filter.<br />
                  <em>Searching across all content types is coming in 2023.</em>
                </span>
                <span v-if="contentType !== 'events' && riskCategoriesSelection"
                  ><span class="application-heading"
                    >Searching for Analysis only.</span
                  ><br />
                  Disable Analysis filters to search for Events.<br />
                  <em
                    >Searching across all content types is coming in 2023.</em
                  ></span
                >
              </v-tooltip>

              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterEvents
              :reset="resetTimestamp"
              @change-events="handleChangeEventsV2"
              @change-hotspots-filter="handleChangeHotspotsFilter"
              @change-sources="handleChangeSource"
              @change-severity="handleChangeSeverity"
              @change-tab="handleChangeEventType"
              :for-live-search="true"
              :topic-filter-counts="topicFilterCounts"
              :selected-topics="eventsSelection"
              :verified-type-selection="verifiedTypeSelection"
              :verified-extras-selection="verifiedExtrasSelection"
              :map-type="mapType"
              :events-type-selection="eventsTypeSelection"
              :hotspots-only-selection="hotspotsOnlySelection"
              :source-filter-counts="sourceFilterCounts"
              :selected-sources="sources"
              @changed-live-search="handleChangeEvents"
              @change-has-topics-selected="handleChangeHasTopicsSelected"
            ></SearchFilterEvents>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- ANALYSIS FILTER -->
        <v-expansion-panel v-if="isLiveSearchAnalysisFilterUnlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Analysis</span>

              <v-tooltip top open-delay="1000" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs" class="switch-w-tooltip">
                    <v-badge
                      v-if="
                        contentType === 'analysis' && riskCategoriesSelection
                      "
                      :title="`There are active analysis filters in this panel`"
                      bordered
                      inline
                      class="ml-1"
                    ></v-badge>
                    <v-badge
                      v-if="
                        contentType &&
                        contentType !== 'analysis' &&
                        eventsSelection
                      "
                      bordered
                      inline
                      color="#d0d0d0"
                      class="ml-1"
                    ></v-badge>
                  </div>
                </template>
                <span v-if="contentType === 'analysis'"
                  ><span class="application-heading"
                    >Searching for Analysis only.</span
                  >
                  <br />
                  To disable Analysis and show Events, disable all Analysis
                  filters.<br />
                  <em>Searching across all content types is coming in 2023.</em>
                </span>
                <span v-if="contentType !== 'analysis'"
                  ><span class="application-heading"
                    >Searching for Events only.</span
                  ><br />
                  Enable an Analysis filter to search for Analysis.<br />
                  <em
                    >Searching across all content types is coming in 2023.</em
                  ></span
                >
              </v-tooltip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterAnalysis
              :reset="resetTimestamp"
              :risk-categories-filter-counts="riskCategoriesFilterCounts"
              :risk-categories-selection="riskCategoriesSelection"
              :risk-category-tags-selection="riskCategoryTagsSelection"
              @change-risk-categories="handleChangeRiskCategories"
              @change-risk-category-tags="handleChangeRiskCategoryTags"
            ></SearchFilterAnalysis>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- DATE RANGE FILTER -->
        <v-expansion-panel v-if="!isLiveSearchEventsFilterV2Unlocked">
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Date Range</span>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterDateRange
              :selectedRange="timeRange"
              :reset="resetTimestamp"
              :date-range-start-days-back="dateRangeStartDaysBack"
              @change="handleChangeDateRange"
            ></SearchFilterDateRange>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- DATE RANGE PICKER FILTER -->
        <v-expansion-panel v-if="isLiveSearchEventsFilterV2Unlocked">
          <v-expansion-panel-header
            class="panel-header justify-self-start date-range"
          >
            <div>
              <span class="panel-header-text">Date Range</span>
              <v-badge
                :content="getDateRangeString()"
                :title="`Results are limited to ${getDateRangeString()}`"
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterDateRangePicker
              :selected-range="timeRange"
              :reset="resetTimestamp"
              :date-range-max-date-back="dateRangeMaxDateBack"
              :date-range-start-days-back="dateRangeStartDaysBack"
              @change="handleChangeDateRange"
            ></SearchFilterDateRangePicker>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- PLACES FILTER -->
        <v-expansion-panel
          v-show="
            isLiveSearchPlacesFilterUnlocked &&
            hasPlaces &&
            !isLiveSearchLocationFilterUnlocked
          "
        >
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Places</span>
              <v-badge
                v-if="placesSelection && placesSelection.length"
                :title="`There are active place filters in this panel.`"
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterPlaces
              :selected="placesSelection"
              :selectedUnitsRadius="placesSelectionUnitsRadius"
              :reset="resetTimestamp"
              @change="handleChangePlaces"
            ></SearchFilterPlaces>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- SOURCE FILTER -->
        <v-expansion-panel
          v-if="!isLiveSearchEventsFilterV2Unlocked"
          class="hyp-exp-panels"
        >
          <v-expansion-panel-header class="panel-header justify-self-start">
            <div>
              <span class="panel-header-text">Sources</span>
              <v-badge
                v-if="
                  this.sources && (this.sources.news || this.sources.social)
                "
                title="There are active filters in this panel."
                bordered
                inline
                class="ml-1"
              ></v-badge>
              <div class="clearfix"></div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <SearchFilterSource
              :reset="resetTimestamp"
              :selected="sources"
              :source-filter-counts="sourceFilterCounts"
              @change="handleChangeSource"
            ></SearchFilterSource>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashForEach from "lodash/forEach";
import lodashSortBy from "lodash/sortBy";
import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { ICONS } from "@/common/constants/icons";
import {
  EVENT_ACTIONS,
  SEARCH_CATEGORIES,
  CONTENT_TYPES,
} from "@/common/constants/analytics";
import {
  convertDatesToRange,
  getTodayStartEndDate,
  nowUtc,
} from "@/common/date.utilities";
import { mapGetters } from "vuex";
import {
  GET_PLACES,
  GET_SAVED_SEARCH_DETAILS_BY_ID,
  GET_SAVED_SEARCH_QUERIES,
  GET_SELECTOR_PANEL,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_SAVED_SEARCH,
  MODULE_PREFIX_HOME,
} from "@/store";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { findTopicAllFlatten } from "@/services/events/topics.service";
import { SEARCH_PLACES_RADIUS } from "@/common/constants/savedSearch";
import { getSavedSearchIdPropertyName } from "@/services/notifications/notifications.service";
import { hasEssentialsAccess } from "@/services/user/user.service";
import { getRiskCategoryByName } from "@/services/controlRisks/risks.service";

const EMITTED_ACTIONS = {
  CHANGE_KEYWORDS: "change-keywords",
  CHANGE_KEYWORDS_BUILDER: "change-keywords-builder",
  CHANGE_DATE_RANGE: "change-date-range",
  CHANGE_BOUNDS: "change-bounds",
  CHANGE_PLACES: "change-places",
  CHANGE_LOCATIONS: "change-locations",
  CHANGE_EVENTS: "change-events",
  CHANGE_EVENTS_V2: "change-events-v2",
  CHANGE_SOURCES: "change-sources",
  CHANGE_HAS_FILTERS_SET: "change-has-filters-set",
  CHANGE_SHOULD_PULL_DATA: "change-should-pull-data",
  RESET: "reset",
  CHANGE_SAVED_SEARCH_QUERY: "change-saved-search-query",
  CHANGE_HOTSPOTS_FILTER: "change-hotspots-filter",
  CHANGE_TAB: "change-tab",
  CHANGE_RISK_CATEGORIES: "change-risk-categories",
  CHANGE_SEVERITY: "change-severity",
  CHANGE_RISK_CATEGORY_TAGS: "change-risk-category-tags",
};

export default {
  name: "SearchContentFilters",
  components: {
    SearchFilterDateRange: () =>
      import("@/components/search/SearchFilterDateRange.vue"),
    SearchFilterDateRangePicker: () =>
      import("@/components/search/SearchFilterDateRangePicker.vue"),
    SearchFilterEvents: () => import("@/components/search/SearchFilterEvents"),
    EventsPicker: () => import("@/components/events/EventsPicker"),
    SearchFilterSource: () => import("@/components/search/SearchFilterSource"),
    SearchFilterPlaces: () => import("@/components/search/SearchFilterPlaces"),
    SearchFilterLocations: () =>
      import("@/components/search/SearchFilterLocations"),
    SearchFilterSavedQueries: () =>
      import("@/components/search/SearchFilterSavedQueries"),
    SearchFilterAnalysis: () =>
      import("@/components/search/SearchFilterAnalysis"),
    SearchFilterKeywords: () =>
      import("@/components/search/SearchFilterKeywords"),
  },
  props: {
    dateRangeStartDaysBack: {
      type: Number,
    },
    dateRangeMaxDateBack: {
      type: String,
    },
    filterCounts: {
      type: Object,
      default: () => {},
    },
    resetTimestamp: {
      type: String,
      default: new Date().toISOString(),
    },
    currentContentType: {
      type: String,
      default: CONTENT_TYPES.events,
    },
  },
  data() {
    const hasEssentials = hasEssentialsAccess();
    return {
      hasEssentials: hasEssentials,
      boundsType: "geography",
      keywords: null,
      keywordsBuilder: null,
      filterPanelsOpen: 1,
      sources: null,
      timeRange: null,
      hasTopicsSelected: false,
      hasEventsV2Selected: false,
      events: null,
      eventsSelection: null,
      eventsTypeSelection: null,
      verifiedTypeSelection: null,
      verifiedExtrasSelection: null,
      mapType: null,
      hotspotsOnlySelection: null,
      selectedSavedSearch: null,
      rememberedSavedSearchId: null,
      defaultSavedSearchId: this.$route.params?.searchid,
      placesSelection: null,
      locationSelectionAoiId: null,
      locationSelectionPlaces: null,
      placesSelectionUnitsRadius: null,
      riskCategoriesSelection: null,
      hasRiskCategoriesSelected: false,
      contentType: null,
      riskCategoryTagsSelection: null,
    };
  },
  async mounted() {
    await this.getPlaces();
    await this.loadQueries();

    // handle deep link in URL
    if (lodashGet(this.$route, "params.searchid")) {
      this.rememberedSavedSearchId = this.$route.params.searchid;
      this.$store.dispatch(
        MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
        this.rememberedSavedSearchId
      );
      this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, true);
      return;
    }

    if (this.preferences && this.preferences.liveSearch) {
      let lsPrefs = this.preferences.liveSearch;
      if (lsPrefs.selectedSavedSearch) {
        this.rememberedSavedSearchId = lsPrefs.selectedSavedSearch;
        this.$store.dispatch(
          MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
          this.rememberedSavedSearchId
        );
        return;
      }

      if (lsPrefs.riskCategories) {
        this.contentType = CONTENT_TYPES.analysis;
        this.riskCategoriesSelection = lsPrefs.riskCategories;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES,
          this.riskCategoriesSelection
        );
      } else {
        this.riskCategoriesSelection = null;
      }

      if (lsPrefs.tags) {
        this.riskCategoryTagsSelection = lsPrefs.tags;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS,
          this.riskCategoryTagsSelection
        );
      } else {
        this.riskCategoryTagsSelection = null;
      }

      if (lsPrefs.boundsType) {
        this.boundsType = lsPrefs.boundsType;
        this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, this.boundsType, true);
      }
      if (this.isLiveSearchLocationFilterUnlocked) {
        if (lsPrefs.aoiId || lsPrefs.places) {
          const places = this.getSelectedPlacesFeatures(lsPrefs.places);
          this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
            boundsType: lsPrefs.boundsType,
            bounds: lsPrefs.boundsType === "mapextent" ? this.bounds : null,
            places: places,
            radius: lsPrefs.placesRadius,
            poisDistUnits: lsPrefs.placesPoisDistUnits,
            aoiId: lsPrefs.aoiId,
          });
          this.locationSelectionAoiId = lsPrefs.aoiId;
          this.locationSelectionPlaces = lsPrefs.places;
        }
      } else {
        if (
          lsPrefs.places &&
          lsPrefs.placesRadius &&
          lsPrefs.placesPoisDistUnits
        ) {
          const places = this.getSelectedPlacesFeatures(lsPrefs.places);
          this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
            radius: lsPrefs.placesRadius,
            poisDistUnits: lsPrefs.placesPoisDistUnits,
            places: places,
          });
          this.placesSelection = lsPrefs.places;
        }
      }

      if (lsPrefs.source) {
        this.sources = {};
        let constructingSources = {
          social: false,
          news: false,
        };
        lodashForEach(lsPrefs.source.split(","), (source) => {
          if (source === "twitter") {
            constructingSources.social = true;
          }
          if (source === "news") {
            constructingSources.news = true;
          }
        });
        this.sources = constructingSources;
        this.$emit(EMITTED_ACTIONS.CHANGE_SOURCES, this.sources);
      }

      //Prefs for keywords
      if (
        lodashGet(lsPrefs.keywordsBuilder, "includes[0].keywords.length") ||
        lodashGet(lsPrefs.keywordsBuilder, "excludes.length")
      ) {
        this.keywordsBuilder = lsPrefs.keywordsBuilder;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER,
          this.keywordsBuilder
        );
      } else if (lsPrefs.search) {
        this.keywords = lsPrefs.search;
        this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, this.keywords);
      }

      if (lsPrefs.topic || lsPrefs.topics) {
        let topics = lsPrefs.topic || lsPrefs.topics;
        this.eventsSelection = topics;
      }
      if (lsPrefs.verifiedType) {
        this.verifiedTypeSelection = lsPrefs.verifiedType;
        this.verifiedExtrasSelection = {
          sectors: lsPrefs.sectors || null,
          assets: lsPrefs.assets || null,
          attackTypes: lsPrefs.attackTypes || null,
          perpetrators: lsPrefs.perpetrators || null,
          severityMin: lsPrefs.severityMin || 0,
          severityMax: lsPrefs.severityMax || 10,
        };
      }
      if (lsPrefs.mapType) {
        this.mapType = lsPrefs.mapType;
      }

      this.eventsTypeSelection = lsPrefs.eventType || "all";
      this.hotspotsOnlySelection = !!lsPrefs.hotspotsOnly || false;
      this.riskCategoriesSelection = lsPrefs.riskCategories || null;
      this.riskCategoryTagsSelection = lsPrefs.tags || null;
      this.selectedSavedSearch = null;

      this.handleChangeEventsV2({
        assets: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.assets
          : null,
        eventType: this.eventsTypeSelection,
        events: this.eventsSelection,
        sectors: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.sectors
          : null,
        attackTypes: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.attackTypes
          : null,
        perpetrators: this.verifiedExtrasSelection
          ? this.verifiedExtrasSelection.perpetrators
          : null,
        //TODO maybe this could be constructed better?
        severity: this.verifiedExtrasSelection
          ? [
              this.verifiedExtrasSelection.severityMin,
              this.verifiedExtrasSelection.severityMax,
            ]
          : null,
        verifiedType: this.verifiedTypeSelection,
        mapType: this.mapType,
        riskCategoriesSelection: this.riskCategoriesSelection,
      });
    } else {
      this.$emit(EMITTED_ACTIONS.CHANGE_SHOULD_PULL_DATA, false);
    }

    this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, this.hasFiltersSet);
  },
  computed: {
    ...mapGetters({
      queries: MODULE_PREFIX_SAVED_SEARCH + "queries",
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
      places: MODULE_PREFIX_PLACES + "places",
      savedSearchDetails: MODULE_PREFIX_SAVED_SEARCH + "savedSearchDetails",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    isLiveSearchLocationFilterUnlocked() {
      return this.featureUnlocked("livesearch-filter-locations");
    },
    isLiveSearchEventsFilterV2Unlocked() {
      return this.featureUnlocked("livesearch-filter-events-v2");
    },
    isLiveSearchPlacesFilterUnlocked() {
      return this.featureUnlocked("livesearch-filter-places");
    },
    isLiveSearchInfoModalUnlocked() {
      return this.featureUnlocked("livesearch-search-info-modal");
    },
    isLiveSearchVisualKeywordUnlocked() {
      return this.featureUnlocked("livesearch-filter-advanced");
    },
    isLiveSearchAnalysisFilterUnlocked() {
      return this.featureUnlocked("livesearch-filter-analysis");
    },
    hasPlaces() {
      return (
        this.places && this.places.length && !this.places[0].isDefaultLayer
      );
    },
    imgSrc() {
      if (
        lodashGet(ICONS, "misc.datePicker.inactive") &&
        lodashGet(ICONS, "menu.events.inactive")
      ) {
        const dateRange = ICONS.misc.datePicker.inactive;
        const events = ICONS.menu.events.inactive;
        return {
          dateRange: require(`@/assets/images${dateRange}`),
          events: require(`@/assets/images${events}`),
        };
      }
      return null;
    },
    sourceFilterCounts() {
      return lodashGet(this.filterCounts, "sourceTotals");
    },
    topicFilterCounts() {
      return lodashGet(this.filterCounts, "topicTotals");
    },
    hasFiltersSet() {
      if (this.hotspotsOnlySelection) {
        return true;
      }
      if (
        this.eventsTypeSelection !== (this.hasEssentials ? "verified" : "all")
      ) {
        return true;
      }
      if (this.hasTopicsSelected) {
        return true;
      }
      if (this.hasEventsV2Selected) {
        return true;
      }
      if (this.hasKeywordsFilterSet) {
        return true;
      }
      if (this.sources && (this.sources.news || this.sources.social)) {
        return true;
      }
      if (this.placesSelection) {
        return true;
      }
      if (this.locationSelectionPlaces || this.locationSelectionAoiId) {
        return true;
      }
      if (this.placesSelectionUnitsRadius) {
        return true;
      }
      if (this.boundsType === "mapextent" || this.boundsType === "worldwide") {
        return true;
      }
      if (
        this.timeRange &&
        JSON.stringify(this.timeRange) !== JSON.stringify([-7, 0])
      ) {
        return true;
      }
      if (this.selectedSavedSearch) {
        return true;
      }
      if (this.riskCategoriesSelection) {
        return true;
      }
      if (this.riskCategoryTagsSelection) {
        return true;
      }
      return false;
    },
    shouldPullData() {
      if (this.hasTopicsSelected) {
        return true;
      }
      if (this.hasEventsV2Selected) {
        return true;
      }
      if (this.hasKeywordsFilterSet) {
        return true;
      }
      if (this.sources && (this.sources.news || this.sources.social)) {
        return true;
      }
      if (
        this.timeRange &&
        JSON.stringify(this.timeRange) !== JSON.stringify([-7, 0])
      ) {
        return true;
      }
      if (this.selectedSavedSearch) {
        return true;
      }
      if (this.riskCategoryTagsSelection) {
        return true;
      }
      return false;
    },
    filterResetIconSrc() {
      return require("@/assets/images/icons/reset_filters.png");
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    riskCategoriesFilterCounts() {
      return lodashGet(this.filterCounts, "riskCategoryTotals");
    },
    isCyberSelected() {
      const cyberId = getRiskCategoryByName("cyber", "id");
      const selection =
        this.riskCategoriesSelection && this.riskCategoriesSelection.length > 0
          ? this.riskCategoriesSelection.split(",")
          : [];
      return selection.includes(cyberId);
    },
    hasKeywordsFilterSet() {
      return (
        !!lodashGet(this.keywordsBuilder, "includes[0].keywords.length") ||
        !!lodashGet(this.keywordsBuilder, "excludes.length") ||
        !!this.keywords
      );
    },
  },
  methods: {
    async loadQueries(skipGet) {
      this.loadingQueries = true;
      let SORTED = [];
      try {
        if (!skipGet) {
          await this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_QUERIES,
            {}
          );
        }
        SORTED = lodashSortBy(this.queries, "name");
      } catch (error) {
        console.error("Error in loadQueries: " + JSON.stringify(error));
      }
      this.savedQueries = SORTED;
      this.loadingQueries = false;
    },
    async getPlaces() {
      await this.$store.dispatch(MODULE_PREFIX_PLACES + GET_PLACES);
    },
    handleChangeKeywords(keywords) {
      this.keywords = keywords;
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, this.keywords);
    },
    handleClearKeywords() {
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_keyword,
        EVENT_ACTIONS.clear,
        "Clear"
      );
    },
    handleChangeSource(sources) {
      this.sources = sources;
      this.$emit(EMITTED_ACTIONS.CHANGE_SOURCES, sources);
      if (sources.news) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_sources,
          EVENT_ACTIONS.clicked,
          "news"
        );
      }
      if (sources.social) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_sources,
          EVENT_ACTIONS.clicked,
          "social"
        );
      }
    },
    handleChangeDateRange(dates) {
      this.timeRange = dates.range;
      this.$emit(EMITTED_ACTIONS.CHANGE_DATE_RANGE, dates);
      if (dates.start) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_start_date,
          EVENT_ACTIONS.changed,
          "start date to " + dates.start
        );
      }
      if (dates.end) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_end_date,
          EVENT_ACTIONS.changed,
          "end date to " + dates.end
        );
      }
    },
    handleChangeBounds() {
      this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, {
        boundsType: this.boundsType,
        bounds: this.bounds,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_bounds,
        EVENT_ACTIONS.changed,
        "bounds to " + this.bounds
      );
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_bounds_type,
        EVENT_ACTIONS.changed,
        "boundsType to " + this.boundsType
      );
    },
    getSelectedPlacesFeatures(selected) {
      const places = [];
      this.places.forEach((layer) => {
        if (lodashGet(layer, "data.features") && layer.data.features.length) {
          const len = layer.data.features.length;
          for (let i = 0; i < len; i++) {
            const place = layer.data.features[i];
            place.properties["map_icon_name"] = layer.icons
              ? layer.icons
              : "default";
            place.properties["map_layer_name"] = layer.name ? layer.name : null;
            if (
              lodashGet(place, "id") &&
              selected &&
              selected.includes(place.id) &&
              lodashGet(place, "geometry.coordinates")
            ) {
              places.push(place);
            }
          }
        }
      });
      return places;
    },
    handleChangeLocation(params) {
      let placesIds = null;
      let places = null;
      let radius = null;
      let poisDistUnits = null;
      if (params.places && params.places.length) {
        if (Array.isArray(params.places[0])) {
          placesIds = params.places.map((place) => {
            radius = place[2] || SEARCH_PLACES_RADIUS;
            poisDistUnits = place[4] || "mi";
            return place[3] || null;
          });
          radius = radius;
          poisDistUnits = poisDistUnits;
        } else {
          placesIds = params.places;
        }
        places = this.getSelectedPlacesFeatures(placesIds);
      }

      this.locationSelectionPlaces = placesIds;
      this.locationSelectionAoiId = params.aoiId;

      this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
        boundsType: params.boundsType,
        bounds: params.boundsType === "mapextent" ? this.bounds : null,
        places: places,
        radius: radius || params.radius,
        poisDistUnits: poisDistUnits || params.poisDistUnits,
        aoiId: params.aoiId,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_places,
        EVENT_ACTIONS.changed,
        "location changed"
      );
    },
    handleChangePlaces(params) {
      this.placesSelection = params.places;
      const places = this.getSelectedPlacesFeatures(params.places);
      this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
        places: places,
        radius: params.radius,
        poisDistUnits: params.poisDistUnits,
      });
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_places,
        EVENT_ACTIONS.changed,
        "places changed"
      );
    },
    handleChangeEvents(event, events, eventType) {
      //todo update eventsSelection here?
      this.$emit(EMITTED_ACTIONS.CHANGE_EVENTS, events, eventType);
      let evt = events[events.length - 1];
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search_events,
        EVENT_ACTIONS.clicked,
        evt
      );
    },
    handleChangeHasTopicsSelected(val) {
      this.hasTopicsSelected = val;
    },
    handleChangeEventsV2(params) {
      let hasEventsV2Selected = false;
      if (params) {
        if (params.riskCategoriesSelection) {
          hasEventsV2Selected = false;
        } else if (params.events && params.events.length > 0) {
          hasEventsV2Selected = true;
        } else if (params.sectors && params.sectors.length > 0) {
          hasEventsV2Selected = true;
        } else if (params.assets && params.assets.length > 0) {
          hasEventsV2Selected = true;
        }
      }
      this.hasEventsV2Selected = hasEventsV2Selected;
      if (!this.hotspotsOnlySelection) {
        this.eventsTypeSelection = params.eventType;
      }

      if (!params.riskCategoriesSelection) {
        this.mapType = params.mapType || this.mapType;

        this.$emit(EMITTED_ACTIONS.CHANGE_EVENTS_V2, params);
        this.eventsSelection = params.events;
      }
    },
    handleChangeHotspotsFilter(params) {
      this.hotspotsOnlySelection = params.hotspotsOnly || false;

      this.$emit(EMITTED_ACTIONS.CHANGE_HOTSPOTS_FILTER, params);
      if (params.hotspotsOnly) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_sources,
          EVENT_ACTIONS.clicked,
          "hotspots only"
        );
      }
    },
    reset() {
      //keywords
      this.keywords = "";
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS, "");
      this.keywordsBuilder = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER, null);

      //map
      this.boundsType = "geography";
      this.$emit(EMITTED_ACTIONS.CHANGE_BOUNDS, this.boundsType);

      // locations
      this.locationSelectionPlaces = null;
      this.locationSelectionAoiId = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_LOCATIONS, {
        aoiId: null,
        places: null,
      });

      // places
      this.placesSelection = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
        radius: null,
        places: null,
        poisDistUnits: null,
      });

      //events, sources, time
      this.sources = {};
      this.eventsSelection = "";
      this.hotspotsOnlySelection = false;
      this.eventsTypeSelection = "all";
      this.verifiedTypeSelection = null;
      this.verifiedExtrasSelection = null;
      this.mapType = "marker";

      //analysis
      this.riskCategoriesSelection = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES, null);
      this.riskCategoryTagsSelection = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS, null);

      //saved queries
      this.selectedSavedSearch = null;
      this.$emit(EMITTED_ACTIONS.CHANGE_SAVED_SEARCH_QUERY, null); //TODO: This appears to go nowhere
    },
    handleChangeSavedSearchQuery(inQuery) {
      const query = lodashCloneDeep(inQuery);
      this.selectedSavedSearch = query;
      if (query && query.params) {
        let params = query.params;

        //Get the keyword filter from the saved search
        if (
          lodashGet(params.keywordsBuilder, "includes[0].keywords.length") ||
          lodashGet(params.keywordsBuilder, "excludes.length")
        ) {
          this.keywordsBuilder = params.keywordsBuilder;
          this.keywords = null;
        } else if (params.search) {
          this.keywords = params.search;
          this.keywordsBuilder = null;
        } else {
          this.keywords = null;
          this.keywordsBuilder = null;
        }

        if (params.topics) {
          this.$emit(
            EMITTED_ACTIONS.CHANGE_EVENTS,
            params.topics,
            query.params.eventType
          );
          if (this.isLiveSearchEventsFilterV2Unlocked) {
            this.eventsSelection = params.topics.join(",");
          } else {
            this.eventsSelection = this.constructEventsSelectionFromString(
              params.topics.toString()
            );
          }
        } else {
          this.$emit(EMITTED_ACTIONS.CHANGE_EVENTS, null);
          this.eventsSelection = null;
        }

        if (this.isLiveSearchLocationFilterUnlocked) {
          this.handleChangeLocation(params);
        } else {
          let places = null;
          this.placesSelection = null;
          if (params.places) {
            let radius = "";
            let poisDistUnits = "";

            this.placesSelectionUnitsRadius = {};
            const placesIds = params.places.map((place) => {
              radius = place[2] || SEARCH_PLACES_RADIUS;
              poisDistUnits = place[4] || "mi";
              return place[3] || null;
            });
            places = this.getSelectedPlacesFeatures(placesIds);
            this.placesSelection = placesIds;
            this.placesSelectionUnitsRadius.radius = radius;
            this.placesSelectionUnitsRadius.poisDistUnits = poisDistUnits;
          }
          this.$emit(EMITTED_ACTIONS.CHANGE_PLACES, {
            radius: params.radius || SEARCH_PLACES_RADIUS,
            poisDistUnits: params.poisDistUnits || "mi",
            places: places,
          });
        }

        let _sources = {};
        if (params.sources) {
          lodashForEach(params.sources, (source) => {
            if (source === "twitter") {
              source = "social";
            }
            Object.assign(_sources, {
              [source]: true,
            });
          });
        }
        this.handleChangeSource(_sources);

        this.handleChangeHotspotsFilter({
          hotspotsOnly: query.params.hotspotsOnly || false,
        });
        this.hotspotsOnlySelection = query.params.hotspotsOnly || false;

        this.eventsTypeSelection = query.params.eventType || "";
        this.verifiedTypeSelection = query.params.verifiedType || null;

        if (this.verifiedTypeSelection) {
          this.verifiedExtrasSelection = {
            sectors: query.params.sectors || null,
            assets: query.params.assets || null,
            attackTypes: query.params.attackTypes || null,
            perpetrators: query.params.perpetrators || null,
            severityMin: query.params.severityMin || 0,
            severityMax: query.params.severityMax || 10,
          };
        }

        if (params.riskCategories) {
          this.contentType = CONTENT_TYPES.analysis;
          this.riskCategoriesSelection = params.riskCategories.join(",");
          this.handleChangeRiskCategories(this.riskCategoriesSelection);
        }

        if (params.tags) {
          this.riskCategoryTagsSelection = params.tags.join(",");
          this.handleChangeRiskCategoryTags(this.riskCategoryTagsSelection);
        }

        this.boundsType = params.bounds_type ? params.bounds_type : "geography";
        this.bounds = params.bounds;
        this.handleChangeBounds();

        // Move to map location
        if (this.map && params.bounds) {
          let _bounds = params.bounds.split(",");
          try {
            this.map.fitBounds([
              [_bounds[1], _bounds[0]],
              [_bounds[3], _bounds[2]],
            ]);
          } catch (error) {
            console.error(error);
          }
        }

        if (
          typeof params.time_range === "number" &&
          !lodashGet(this.$route, "query.start")
        ) {
          this.handleChangeDateRange({
            range: [params.time_range, "-0"],
            start: getTodayStartEndDate(
              dayjs(nowUtc).add(params.time_range, "days")
            ).start,
            end: getTodayStartEndDate(dayjs(nowUtc)).end,
          });
        } else {
          if (
            lodashGet(this.$route, "query.start") &&
            lodashGet(this.$route, "query.end")
          ) {
            params.start = this.$route.query.start || params.start;
            params.end = this.$route.query.end || params.end;
          }

          this.handleChangeDateRange({
            range: convertDatesToRange(params.start, params.end),
            start: params.start,
            end: params.end,
          });
        }

        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_saved_queries,
          EVENT_ACTIONS.clicked,
          query[getSavedSearchIdPropertyName()]
        );

        this.$emit(EMITTED_ACTIONS.CHANGE_SAVED_SEARCH_QUERY, query);
      } else {
        this.$emit(EMITTED_ACTIONS.RESET, {});
      }
    },
    constructEventsSelectionFromString(topicsString) {
      let topicApiNames = topicsString ? topicsString.split(",") : [];
      let eventsSelection = {};
      for (let i = 0; i < topicApiNames.length; i++) {
        const cur = topicApiNames[i];
        let topic = findTopicAllFlatten(cur, "apiName");
        if (!topic.parentIds) {
          if (!eventsSelection[topic.apiName]) {
            eventsSelection[topic.apiName] = {
              selected: true,
              apiName: topic.apiName,
              subtopics: [],
            };
          }
          eventsSelection[topic.apiName].selected = true;
        } else {
          let parentTopicKey = topic.parentIds[0];
          if (!eventsSelection[parentTopicKey]) {
            eventsSelection[parentTopicKey] = {
              apiName: topic.parentIds[0],
              subtopics: [],
            };
          }
          eventsSelection[parentTopicKey].subtopics.push(topic.apiName);
        }
      }
      return eventsSelection;
    },
    handleChangeEventType(params) {
      this.$emit(EMITTED_ACTIONS.CHANGE_TAB, params);
    },
    /**
     * Generates a natural language explanation of the Date Range filter
     * @returns {string}
     */
    getDateRangeString() {
      let numDays;

      //Add 1 to the time range since timeRange is inclusive
      //e.g. timeRange=[-29,-27] == 3 days --> 27 days from today to 29 days from today is 3 total days
      if (this.timeRange) {
        numDays = this.timeRange[1] - this.timeRange[0] + 1;
      } else {
        numDays = this.dateRangeStartDaysBack + 1;
      }

      return numDays + (numDays == 1 ? " day" : " days");
    },
    handleChangeRiskCategories(params) {
      if (!params || params === "") {
        this.contentType = CONTENT_TYPES.events;
      }
      this.riskCategoriesSelection = params;
      if (!this.isCyberSelected) {
        this.riskCategoryTagsSelection = null;
        this.$emit(
          EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS,
          this.riskCategoryTagsSelection
        );
      }
      this.$emit(EMITTED_ACTIONS.CHANGE_RISK_CATEGORIES, params);
      const size = params.split(",").length;
      if (size > 1) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_risk_categories,
          EVENT_ACTIONS.clicked,
          params
        );
      }
    },
    handleChangeKeywordsBuilder(keywordsBuilder) {
      this.keywordsBuilder = keywordsBuilder;
      this.$emit(EMITTED_ACTIONS.CHANGE_KEYWORDS_BUILDER, keywordsBuilder);
    },
    handleChangeSeverity(severity) {
      this.severity = severity;
      this.$emit(EMITTED_ACTIONS.CHANGE_SEVERITY, severity);
    },
    handleChangeRiskCategoryTags(params) {
      this.riskCategoryTagsSelection = this.isCyberSelected ? params : null;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_RISK_CATEGORY_TAGS,
        this.riskCategoryTagsSelection
      );
      const size = this.riskCategoryTagsSelection
        ? this.riskCategoryTagsSelection.split(",").length
        : 0;
      if (size > 1) {
        this.handlePageTrackerEvent(
          SEARCH_CATEGORIES.live_search_risk_category_tags,
          EVENT_ACTIONS.clicked,
          params
        );
      }
    },
  },
  watch: {
    /**
     * The last time the search was reset. When the timestamp changes, all search filters watching it will reset themselves
     */
    savedSearchDetails: {
      handler(newVal, oldVal) {
        this.handleChangeSavedSearchQuery(this.savedSearchDetails);
      },
      deep: true,
    },
    resetTimestamp: {
      handler() {
        this.reset();
      },
    },
    hasFiltersSet: {
      handler() {
        this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, this.hasFiltersSet);
      },
    },
    currentContentType: {
      handler(newVal) {
        this.contentType = newVal;
      },
    },
    contentType: {
      handler(newVal) {
        if (newVal === CONTENT_TYPES.events) {
          this.riskCategoriesSelection = null;
          this.riskCategoryTagsSelection = null;
        } else {
          this.eventsSelection = null;
        }
      },
    },
    shouldPullData: {
      handler() {
        this.$emit(
          EMITTED_ACTIONS.CHANGE_SHOULD_PULL_DATA,
          this.shouldPullData
        );
      },
    },
    // handle force opening selectorPanel to specific saved search
    "selectorPanel.selectedData.savedSearchId": {
      handler() {
        if (lodashGet(this.selectorPanel, "selectedData.savedSearchId")) {
          this.rememberedSavedSearchId = this.selectorPanel.selectedData.savedSearchId;
          this.$store.dispatch(
            MODULE_PREFIX_SAVED_SEARCH + GET_SAVED_SEARCH_DETAILS_BY_ID,
            this.rememberedSavedSearchId
          );
          this.$emit(EMITTED_ACTIONS.CHANGE_HAS_FILTERS_SET, true);

          // reset the selectedData object
          const selectorPanel = {};
          selectorPanel.selected = "search";
          selectorPanel.visible = true;
          selectorPanel.selectedData = {};
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
            selectorPanel
          );
          return;
        } else {
          this.$emit(EMITTED_ACTIONS.RESET, {});
        }
      },
    },
  },
};
</script>

<style>
.search-box {
  font-size: 14px;
}
.search-box .v-icon {
  font-size: 14px !important;
}
.search-box.v-text-field.v-text-field--enclosed .v-text-field__details {
  display: none;
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot {
  background-color: rgba(0, 0, 0, 0.06);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot
  input {
  color: rgba(0, 0, 0, 0.6);
}
.search-box.theme--light.v-text-field--solo-inverted.v-input--is-focused,
> .v-input__control,
> .v-input__slot,
.v-label {
  color: rgba(0, 0, 0, 0.6);
}
.bounds-radio-text .v-input--selection-controls .v-input__slot > .v-label,
.v-input--selection-controls .v-radio > .v-label {
  font-size: 12px;
}
.search-box * .v-input__slot {
  padding-left: 4px !important;
  padding-right: 4px !important;
}
</style>

<style scoped>
.hyp-main-container {
  overflow-y: auto;
}
.hyp-exp-panels {
  margin-bottom: 75px;
}
.panel-header {
  height: 60px;
}
.panel-header-text {
  color: #333;
}
.panel-header-icon {
  height: 30px;
  width: 30px;
}
.v-radio >>> .v-label {
  color: #1c1c1c;
}
.reset-filters-icon {
  float: right;
  padding-top: 5px;
  padding-left: 5px;
}
::v-deep .v-badge__badge {
  height: 12px;
  min-width: 12px;
}
::v-deep .date-range .v-badge__badge {
  min-height: 12px;
  height: auto;
}
.panel-toggle {
  display: inline-block;
}
::v-deep .panel-toggle.v-input--is-readonly .v-input--selection-controls__input,
::v-deep
  .panel-toggle.v-input--is-readonly
  .v-input--selection-controls__input
  input {
  cursor: default;
}
::v-deep
  .panel-toggle.v-input--is-readonly
  .v-input--selection-controls__ripple {
  display: none;
}
.switch-w-tooltip {
  width: min-content;
  display: inline-block;
}
</style>
